import React, { useEffect, useRef, useState } from 'react';

export interface BellItem {
  key: string;
  emoji: string;
  label: string;
  sublabel?: string;
  onClick: () => void;
}

interface Props {
  total: number;
  items: BellItem[];
}

/** Header notification bell: a count badge + a dropdown of tappable items. Shared by both layouts. */
const NotificationBell: React.FC<Props> = ({ total, items }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-10 h-10 rounded-full bg-white/20 ring-2 ring-white/40 flex items-center justify-center text-lg relative"
        aria-label="Notifications"
      >
        🔔
        {total > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center leading-none">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl py-2 z-50 text-gray-700 max-h-80 overflow-y-auto">
          <p className="px-4 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">Notifications</p>
          {items.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-400">No notifications</p>
          ) : (
            items.map((it) => (
              <button
                key={it.key}
                onClick={() => { setOpen(false); it.onClick(); }}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3"
              >
                <span className="text-lg leading-none">{it.emoji}</span>
                <span className="min-w-0">
                  <span className="block font-semibold text-gray-700 truncate">{it.label}</span>
                  {it.sublabel && <span className="block text-xs text-gray-400 truncate">{it.sublabel}</span>}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
